import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { devisAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Trash2, ArrowRight, Check, X, Clock, FileText, Edit } from 'lucide-react';

const DevisPage = () => {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    description: '',
    montant_ht: '',
    tva_rate: '20',
    validite_jours: '30',
    date_emission: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadDevis();
  }, []);

  const loadDevis = async () => {
    try {
      const response = await devisAPI.getAll();
      setDevis(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await devisAPI.create({
        ...formData,
        montant_ht: parseFloat(formData.montant_ht),
        tva_rate: parseFloat(formData.tva_rate),
        validite_jours: parseInt(formData.validite_jours),
        date_emission: new Date(formData.date_emission).toISOString()
      });
      toast.success('Devis créé avec succès');
      setDialogOpen(false);
      resetForm();
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedDevis) return;
    
    try {
      await devisAPI.update(selectedDevis.id, {
        client_name: formData.client_name,
        description: formData.description,
        montant_ht: parseFloat(formData.montant_ht),
        tva_rate: parseFloat(formData.tva_rate),
        date_emission: new Date(formData.date_emission).toISOString()
      });
      toast.success('Devis modifié');
      setEditDialogOpen(false);
      resetForm();
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const openEditDialog = (d) => {
    setSelectedDevis(d);
    setFormData({
      client_name: d.client_name,
      description: d.description,
      montant_ht: d.montant_ht.toString(),
      tva_rate: d.tva_rate.toString(),
      validite_jours: '30',
      date_emission: d.date_emission ? d.date_emission.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      description: '',
      montant_ht: '',
      tva_rate: '20',
      validite_jours: '30',
      date_emission: new Date().toISOString().split('T')[0]
    });
    setSelectedDevis(null);
  };

  const handleChangeStatus = async (id, newStatus) => {
    try {
      await devisAPI.update(id, { statut: newStatus });
      const statusLabels = { accepte: 'accepté', refuse: 'refusé', en_attente: 'remis en attente' };
      toast.success(`Devis ${statusLabels[newStatus]}`);
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleConvert = async (id) => {
    try {
      await devisAPI.convert(id);
      toast.success('Devis converti en facture !');
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await devisAPI.delete(id);
      toast.success('Devis supprimé');
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (statut) => {
    const config = {
      accepte: { class: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Accepté', icon: Check },
      en_attente: { class: 'bg-amber-100 text-amber-800 border-amber-300', label: 'En attente', icon: Clock },
      refuse: { class: 'bg-red-100 text-red-800 border-red-300', label: 'Refusé', icon: X }
    };
    const { class: className, label, icon: Icon } = config[statut] || config.en_attente;
    return (
      <Badge className={`${className} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const filteredDevis = devis.filter((d) => {
    const matchSearch = d.client_name.toLowerCase().includes(search.toLowerCase()) ||
                       d.numero.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || d.statut === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    enAttente: devis.filter(d => d.statut === 'en_attente').length,
    acceptes: devis.filter(d => d.statut === 'accepte').length,
    refuses: devis.filter(d => d.statut === 'refuse').length,
    montantEnAttente: devis.filter(d => d.statut === 'en_attente').reduce((sum, d) => sum + d.montant_ht, 0),
    montantAccepte: devis.filter(d => d.statut === 'accepte').reduce((sum, d) => sum + d.montant_ht, 0),
  };

  const tauxTransformation = devis.length > 0 
    ? Math.round((stats.acceptes / devis.length) * 100) 
    : 0;

  return (
    <div className="space-y-6" data-testid="devis-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Devis</h1>
          <p className="text-muted-foreground">Gérez vos propositions commerciales</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-devis-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau devis
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un devis</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Nom du client"
                  required
                  data-testid="devis-client"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Prestation proposée"
                  required
                  data-testid="devis-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant HT (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant_ht}
                    onChange={(e) => setFormData({ ...formData, montant_ht: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="devis-montant"
                  />
                </div>
                <div className="space-y-2">
                  <Label>TVA (%)</Label>
                  <select
                    value={formData.tva_rate}
                    onChange={(e) => setFormData({ ...formData, tva_rate: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="devis-tva"
                  >
                    <option value="0">0%</option>
                    <option value="5.5">5.5%</option>
                    <option value="10">10%</option>
                    <option value="20">20%</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    value={formData.validite_jours}
                    onChange={(e) => setFormData({ ...formData, validite_jours: e.target.value })}
                    data-testid="devis-validite"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'émission</Label>
                  <Input
                    type="date"
                    value={formData.date_emission}
                    onChange={(e) => setFormData({ ...formData, date_emission: e.target.value })}
                    required
                    data-testid="devis-date-emission"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="devis-submit">
                Créer le devis
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog Modifier Devis */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le devis {selectedDevis?.numero}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_ht}
                  onChange={(e) => setFormData({ ...formData, montant_ht: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>TVA (%)</Label>
                <select
                  value={formData.tva_rate}
                  onChange={(e) => setFormData({ ...formData, tva_rate: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="0">0%</option>
                  <option value="5.5">5.5%</option>
                  <option value="10">10%</option>
                  <option value="20">20%</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date d'émission</Label>
              <Input
                type="date"
                value={formData.date_emission}
                onChange={(e) => setFormData({ ...formData, date_emission: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Enregistrer les modifications
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700 font-medium">En attente</p>
            </div>
            <p className="text-2xl font-bold font-['Manrope'] text-amber-800">{stats.enAttente}</p>
            <p className="text-sm text-amber-600">{formatCurrency(stats.montantEnAttente)}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check className="h-4 w-4 text-emerald-600" />
              <p className="text-sm text-emerald-700 font-medium">Acceptés</p>
            </div>
            <p className="text-2xl font-bold font-['Manrope'] text-emerald-800">{stats.acceptes}</p>
            <p className="text-sm text-emerald-600">{formatCurrency(stats.montantAccepte)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <X className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-700 font-medium">Refusés</p>
            </div>
            <p className="text-2xl font-bold font-['Manrope'] text-red-800">{stats.refuses}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Taux de transformation</p>
            <p className="text-2xl font-bold font-['Manrope'] text-primary">{tauxTransformation}%</p>
            <p className="text-sm text-muted-foreground">{stats.acceptes} sur {devis.length} devis</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par client ou numéro..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-devis"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'en_attente', 'accepte', 'refuse'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  data-testid={`filter-${status}`}
                >
                  {status === 'all' && 'Tous'}
                  {status === 'en_attente' && 'En attente'}
                  {status === 'accepte' && 'Acceptés'}
                  {status === 'refuse' && 'Refusés'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Montant TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredDevis.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    Aucun devis trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredDevis.map((d) => (
                  <TableRow key={d.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium font-mono">{d.numero}</TableCell>
                    <TableCell className="font-semibold">{d.client_name}</TableCell>
                    <TableCell className="max-w-[150px] truncate text-muted-foreground">{d.description}</TableCell>
                    <TableCell>{formatDate(d.date_creation)}</TableCell>
                    <TableCell className="font-bold">{formatCurrency(d.montant_ttc)}</TableCell>
                    <TableCell>{getStatusBadge(d.statut)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Modifier */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(d)}
                          className="h-7 px-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        {/* Boutons de statut */}
                        {d.statut !== 'accepte' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangeStatus(d.id, 'accepte')}
                            className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Accepter"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        {d.statut !== 'refuse' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangeStatus(d.id, 'refuse')}
                            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Refuser"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        {d.statut !== 'en_attente' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangeStatus(d.id, 'en_attente')}
                            className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Remettre en attente"
                          >
                            <Clock className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Convertir en facture - UNIQUEMENT si accepté */}
                        {d.statut === 'accepte' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleConvert(d.id)}
                            className="h-7 px-2 text-primary hover:text-primary/80 hover:bg-primary/10"
                            title="Convertir en facture"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Supprimer */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(d.id)}
                          className="h-7 px-2 text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Légende */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-2">Légende des actions :</p>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Edit className="h-4 w-4 text-gray-600" /> Modifier
            </span>
            <span className="flex items-center gap-1">
              <Check className="h-4 w-4 text-emerald-600" /> Accepter
            </span>
            <span className="flex items-center gap-1">
              <X className="h-4 w-4 text-red-600" /> Refuser
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-amber-600" /> Remettre en attente
            </span>
            <span className="flex items-center gap-1">
              <ArrowRight className="h-4 w-4 text-primary" /> Convertir en facture (si accepté)
            </span>
            <span className="flex items-center gap-1">
              <Trash2 className="h-4 w-4 text-gray-400" /> Supprimer
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DevisPage;
